import { Card, LinkButton, Stack, Text, textVariants } from '@etg/wings';
import { Notification, variants, Overlay } from '@eti/components';
import { useTranslation } from '@eti/providers';
import { css, etiColors, mediaQueries } from '@eti/styles';
import { useMemo, useState } from 'react';
import { getProductDescription, getProductNamesFromIDs } from '../../utils';
import type { PreSelectedProductItem } from '../model';
import PreSelectedProductsList from './PreSelectedProductsList';

const cardStyles = css`
  margin-bottom: 16px;
  margin-top: 8px;
  max-width: 540px;

  @media ${mediaQueries.medium.up} {
    margin-bottom: 0;
    margin-top: 16px;
  }
`;

const textStyles = css`
  color: #737373;
`;

const drawerTextStyles = css`
  color: ${etiColors.black};
  line-height: 24px;

  & > ul {
    list-style: disc;
    margin-inline-start: 8px;
    padding-inline-start: 8px;
  }
`;

interface PreSelectedProductsProps {
  preSelectedExtraProducts: PreSelectedProductItem[];
  commonProductsInFare?: number[];
  productsHandler: (productId: number) => void;
  manuallyAddedProductsId: number[];
  manuallyRemovedProductsId: number[];
}

const PreSelectedProducts = ({
  preSelectedExtraProducts,
  commonProductsInFare = [],
  productsHandler,
  manuallyAddedProductsId = [],
  manuallyRemovedProductsId = [],
}: PreSelectedProductsProps) => {
  const { t } = useTranslation();
  const [showProductDetails, setShowProductDetails] = useState(false);
  const toggleProductDetailsDrawer = () => {
    setShowProductDetails(!showProductDetails);
  };

  const productIdNameMap = useMemo(() => {
    return preSelectedExtraProducts.reduce<Record<string, string>>((map, product) => {
      const mappedProduct = { ...map };
      mappedProduct[product.productId] = product.name;
      return mappedProduct;
    }, {});
  }, [preSelectedExtraProducts]);

  const filteredProductIds = commonProductsInFare.filter(
    (productId) =>
      !manuallyAddedProductsId.includes(productId) &&
      !manuallyRemovedProductsId.includes(productId),
  );

  const productNames = getProductNamesFromIDs(filteredProductIds, productIdNameMap, t);

  const commonProductsInFareMinusManuallyAdded = commonProductsInFare.filter(
    (productId) => !manuallyAddedProductsId.includes(productId),
  );

  const commonProductsInFareMinusManuallyAddedPlusManuallyRemoved = [
    ...new Set([...commonProductsInFareMinusManuallyAdded, ...manuallyRemovedProductsId]),
  ];

  const pluralProductsTranslationKey = t(
    'YourSelectedTrip.Preselected.RemovedProductPlural.Information.Text',
    productNames,
  );

  const singularProductTranslationKey = t(
    'YourSelectedTrip.Preselected.RemovedProduct.Information.Text',
    productNames,
  );

  const notificationMessage =
    filteredProductIds.length > 1 ? pluralProductsTranslationKey : singularProductTranslationKey;

  return (
    <>
      <Card className={cardStyles} data-testid="preSelected-extra-products-section">
        <Stack spacing={8}>
          <Text data-testid="preselected-products-title" variant={textVariants._16_BOLD}>
            {t('YourSelectedTrip.Preselected.Product.Title')}
          </Text>
          <Text className={textStyles} data-testid="preselected-products-subtitle">
            {t('Price.Per.Adult')}
          </Text>
          <PreSelectedProductsList
            commonProductsInFare={commonProductsInFare}
            preSelectedExtraProducts={preSelectedExtraProducts}
            productsHandler={productsHandler}
            removedProductsId={commonProductsInFareMinusManuallyAddedPlusManuallyRemoved}
          />
          {filteredProductIds.length > 0 && (
            <Notification dataTestId="product-information" variant={variants.INFORMATION}>
              {notificationMessage}
            </Notification>
          )}
          <LinkButton
            data-testid="product-details-link"
            onClick={() => toggleProductDetailsDrawer()}
          >
            {t('YourSelectedTrip.Preselected.ProductDetails.Link.Label')}
          </LinkButton>
        </Stack>
      </Card>
      <Overlay.Drawer
        dataTestId="product-details-drawer"
        handleClose={() => setShowProductDetails(false)}
        isOpen={showProductDetails}
      >
        <Overlay.Header
          onClose={() => setShowProductDetails(false)}
          title={t('YourSelectedTrip.Product.Details.Drawer.Title')}
        />
        <Overlay.Content data-testid="product-details-drawer-content">
          {preSelectedExtraProducts?.map((product: PreSelectedProductItem) => {
            const { productId, name } = product;
            const productDescription = getProductDescription(productId, t);
            return (
              <Card key={`${productId}-${name}`}>
                <Stack spacing={8}>
                  <Text className={drawerTextStyles} variant={textVariants._16_BOLD}>
                    {name}
                  </Text>
                  <Text asHtml className={drawerTextStyles} variant={textVariants._16}>
                    {productDescription}
                  </Text>
                </Stack>
              </Card>
            );
          })}
        </Overlay.Content>
      </Overlay.Drawer>
    </>
  );
};

export default PreSelectedProducts;
